import { useEffect, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from "react";

// A tiny History-API router: the site only needs a handful of URL shapes,
// so a dependency would be overkill.

const listeners = new Set<() => void>();
const notify = () => listeners.forEach(l => l());

if (typeof window !== "undefined") window.addEventListener("popstate", notify);

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function navigate(to: string) {
  if (to === window.location.pathname + window.location.search) return;
  window.history.pushState(null, "", to);
  window.scrollTo(0, 0);
  notify();
}

export function useLocation() {
  const path = useSyncExternalStore(subscribe, () => window.location.pathname + window.location.search);
  const url = new URL(path, window.location.origin);
  return { pathname: url.pathname, search: url.searchParams };
}

export function Link({ href, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!href.startsWith("/")) return;
    e.preventDefault();
    navigate(href);
  };
  return <a href={href} onClick={handle} {...rest} />;
}

export function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}
